/**
 * TODO: Each of the hooks in this file are only used in one place. Eliminate this file and move the code inline where it is used?
 */

import moment from 'moment';
import { useEffect, useState } from 'react';

import { DashboardApi } from 'Api/Dashboards/DashboardApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getOrganizations } from 'Api/UcrmTprm/UcrmTprmApi';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DateRange } from 'Hooks/DateFilter';
import { OrganizationResponseWithAssessmentSubjectsBase } from 'Models/AssessmentSubject';
import { DueDiligenceDashboardAssessmentSubject, DueDiligenceDashboardCompletedRiskAssessment, DueDiligenceDashboardMonthlyRiskPortfolioSummary } from 'Models/Dashboards';
import { ActiveState } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { getOrganizationAssessmentSubjects } from 'Models/UcrmTprm';

/**
 * Fetches the module's organizations (TPRM third parties or UCRM businesses), keeping only active organizations and their active assessment subjects (services or use cases).
 * Each assessment subject is extended with `is_overdue` and `is_upcoming`, based on its due diligence workflow due date. Nothing is fetched unless `userIsAuthorized`.
 * @returns The active organizations, a flat list of their active (extended) assessment subjects, and an error message if the request failed.
 */
export const useActiveOrganizationsAndAssessmentSubjectsQuery = (api: ClientTPRMApi | ClientUcrmApi, userIsAuthorized: boolean) => {
    const [activeOrganizations, setActiveOrganizations] = useState<OrganizationResponseWithAssessmentSubjectsBase[]>();
    const [activeAssessmentSubjects, setActiveAssessmentSubjects] = useState<DueDiligenceDashboardAssessmentSubject[]>();
    const [organizationsError, setOrganizationsError] = useState<string>();

    useEffect(() => {
        const fetchActiveOrganizationsAndAssessmentSubjects = async (): Promise<void> => {
            try {
                const activeOrganizationsWithActiveSubjects = removeInactiveOrganizationsAndAssessmentSubjects(api.module, (await getOrganizations(api)).data);
                const assessmentSubjects = activeOrganizationsWithActiveSubjects.flatMap((organization) => getOrganizationAssessmentSubjects(api.module, organization));

                const extendedAssessmentSubjects: DueDiligenceDashboardAssessmentSubject[] = assessmentSubjects.map((assessmentSubject) => {
                    const isOverdue = assessmentSubject.assessment_workflow_setup.due_date !== undefined && moment(assessmentSubject.assessment_workflow_setup.due_date).isBefore();
                    const isUpcoming = assessmentSubject.assessment_workflow_setup.due_date !== undefined && moment(assessmentSubject.assessment_workflow_setup.due_date).isAfter();

                    return {
                        ...assessmentSubject,
                        is_overdue: !!isOverdue,
                        is_upcoming: !!isUpcoming,
                    };
                });

                setActiveOrganizations(activeOrganizationsWithActiveSubjects);
                setActiveAssessmentSubjects(extendedAssessmentSubjects);
            } catch (err) {
                setOrganizationsError(err.message);
            }
        };

        if (userIsAuthorized) {
            fetchActiveOrganizationsAndAssessmentSubjects();
        }
    }, [api, userIsAuthorized]);

    return { activeOrganizations, activeAssessmentSubjects, organizationsError };
};

export const useCompletedRiskAssessmentsQuery = (api: DashboardApi, module: DueDiligenceModule, dateRange: DateRange) => {
    const [completedRiskAssessments, setCompletedRiskAssessments] = useState<DueDiligenceDashboardCompletedRiskAssessment[]>();
    const [completedRiskAssessmentsError, setCompletedRiskAssessmentsError] = useState<string>();

    useEffect(() => {
        const fetchCompletedRiskAssessmentsData = async (): Promise<void> => {
            try {
                const completedRiskAssessmentsResponse = await api.getCompletedRiskAssessments(module, jsDateToIso8601(dateRange.startDate), jsDateToIso8601(dateRange.endDate));
                setCompletedRiskAssessments(completedRiskAssessmentsResponse.data);
            } catch (err) {
                setCompletedRiskAssessmentsError(err.message);
            }
        };

        fetchCompletedRiskAssessmentsData();
    }, [api, dateRange, module]);

    return { completedRiskAssessments, completedRiskAssessmentsError };
};

export const useRiskPortfolioHistoryQuery = (api: DashboardApi, module: DueDiligenceModule, dateRange: DateRange) => {
    const [history, setHistory] = useState<DueDiligenceDashboardMonthlyRiskPortfolioSummary[]>();
    const [historyError, setHistoryError] = useState<string>();

    useEffect(() => {
        const fetchRiskPortfolioHistory = async (): Promise<void> => {
            try {
                const historyResponse = await api.getRiskPortfolioHistory(module, jsDateToIso8601(dateRange.startDate), jsDateToIso8601(dateRange.endDate));
                setHistory(historyResponse.data);
            } catch (err) {
                setHistoryError(err.message);
            }
        };

        fetchRiskPortfolioHistory();
    }, [api, dateRange, module]);

    return { riskPortfolioHistory: history, riskPortfolioHistoryError: historyError };
};

/** Returns a new array that does not include inactive organizations or inactive assessment subjects. */
const removeInactiveOrganizationsAndAssessmentSubjects = (module: DueDiligenceModule, organizations: OrganizationResponseWithAssessmentSubjectsBase[]): OrganizationResponseWithAssessmentSubjectsBase[] => {
    const activeOrganizations = organizations.filter((organization) => organization.active_state === ActiveState.ACTIVE);
    return activeOrganizations.map((organization) => {
        const activeSubjects = getOrganizationAssessmentSubjects(module, organization).filter((subject) => subject.active_state === ActiveState.ACTIVE);
        // Preserve whichever key the module uses for its assessment subjects (`services` in TPRM, `use_cases` in UCRM).
        return module === ProductModule.UCRM ? { ...organization, use_cases: activeSubjects } : { ...organization, services: activeSubjects };
    });
};
